import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Check,
  Clock,
  FileText,
  Shield,
  Calendar,
  DollarSign,
  Activity,
  Building2,
  Eye,
  Search,
  Download,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import Modal from './Modal';
import CaseFeed from './CaseFeed';
import VerdictView from './VerdictView';
import {
  STATUS_CONFIG,
  type Bill,
  type BillStatus,
  type BillUpdate,
  type BillCommunicationLog,
} from '../types/bill';

export type DetailTab = 'progress' | 'caselog' | 'document';

type BillDetailModalProps = {
  bill: Bill;
  tab: DetailTab;
  onTabChange: (t: DetailTab) => void;
  onClose: () => void;
  updates: BillUpdate[];
  commLogs: BillCommunicationLog[];
  loadingData: boolean;
  hasUnread?: boolean;
};

export default function BillDetailModal({
  bill,
  tab,
  onTabChange,
  onClose,
  updates,
  commLogs,
  loadingData,
  hasUnread,
}: BillDetailModalProps) {
  const navigate = useNavigate();
  const billAmount = bill.bill_amount || 0;
  const amountSaved = bill.amount_saved || 0;
  const currentAmount = billAmount - amountSaved;
  const pct = billAmount > 0 ? Math.round((amountSaved / billAmount) * 100) : 0;
  const title =
    bill.provider_name ||
    bill.display_name ||
    bill.file_path.split('/').pop() ||
    'Invoice';
  const statusCfg = STATUS_CONFIG[bill.status];

  const steps = getProgressSteps(bill.status, bill.created_at, updates);

  /* Build info fields */
  const infoFields: { icon: React.ReactNode; label: string; value: string }[] =
    [];
  if (bill.insurance_carrier) {
    infoFields.push({
      icon: <Shield size={15} />,
      label: 'Insurance',
      value: bill.insurance_carrier,
    });
  }
  if (bill.date_of_service) {
    infoFields.push({
      icon: <Calendar size={15} />,
      label: 'Service Date',
      value: new Date(bill.date_of_service).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    });
  }
  if (bill.patient_amount_due != null) {
    infoFields.push({
      icon: <DollarSign size={15} />,
      label: 'Amount Due',
      value: `$${bill.patient_amount_due.toLocaleString()}`,
    });
  }

  return (
    <Modal open onClose={onClose} className="detail-modal">
      {/* Header */}
      <div className="detail-header">
        <div className="detail-title-row">
          <div className="detail-title-left">
            <div className="detail-provider-icon">
              <Building2 size={22} />
            </div>
            <div className="detail-title-text">
              <h2 className="detail-title">{title}</h2>
              <div className="detail-subtitle-row">
                <span className={`status-badge ${statusCfg.className}`}>
                  {statusCfg.label}
                </span>
                <span className="detail-date">
                  Uploaded{' '}
                  {new Date(bill.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
                {bill.bill_amount != null && (
                  <span className="detail-amount-tag">
                    ${bill.bill_amount.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Info Cards + Savings, floating section between header and tabs */}
      {(amountSaved > 0 || infoFields.length > 0) && (
        <div className="detail-cards-section">
          {/* Savings */}
          {amountSaved > 0 && (
            <div className="detail-savings-card">
              <img
                src="/images/mascot/fox-cutting-bill.png"
                alt=""
                style={{
                  width: 56,
                  height: 'auto',
                  position: 'absolute',
                  bottom: 6,
                  right: 10,
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))',
                  zIndex: 1,
                  opacity: 0.85,
                }}
              />
              <div className="detail-savings-left">
                <span className="detail-savings-label">Your Savings</span>
                <strong className="detail-savings-amount">
                  ${amountSaved.toLocaleString()}
                </strong>
                <span className="detail-savings-pct">{pct}% off</span>
              </div>
              <div className="detail-savings-right">
                <span className="detail-savings-sub">Original</span>
                <span className="detail-savings-strike">
                  ${billAmount.toLocaleString()}
                </span>
                <span className="detail-savings-sub">Now</span>
                <strong className="detail-savings-current">
                  ${currentAmount.toLocaleString()}
                </strong>
              </div>
            </div>
          )}

          {/* Info fields */}
          {infoFields.length > 0 && (
            <div className="bill-info-summary">
              {infoFields.map((f, i) => (
                <div key={i} className="bill-info-item">
                  <span className="bill-info-icon">{f.icon}</span>
                  <div>
                    <span className="bill-info-label">{f.label}</span>
                    <span className="bill-info-value">{f.value}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="detail-tabs">
        {([
          { key: 'progress' as const, icon: <Clock size={14} />, label: 'Progress' },
          { key: 'document' as const, icon: <Eye size={14} />, label: 'Bill' },
          { key: 'caselog' as const, icon: <Activity size={14} />, label: 'Case Activity' },
        ]).map((t) => (
          <button
            key={t.key}
            className={`detail-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => onTabChange(t.key)}
          >
            {t.icon}
            <span>{t.label}</span>
            {t.key === 'caselog' && hasUnread && tab !== 'caselog' && (
              <span className="tab-unread-dot" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="detail-body">
        {tab === 'progress' && bill.status === 'completed' && (
          /* v3 Verdict view replaces the standard progress timeline for
             closed cases, the moment that earns the renewal. */
          <VerdictView bill={bill} />
        )}
        {tab === 'progress' && bill.status !== 'completed' && (
          <div className="progress-timeline">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`timeline-item ${step.done ? 'is-done' : ''}`}
              >
                <div className="timeline-marker-col">
                  <div className={`timeline-dot ${step.done ? 'done' : ''}`}>
                    {step.done ? <Check size={16} /> : <Clock size={14} />}
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`timeline-line ${step.done ? 'line-done' : ''}`}
                    />
                  )}
                </div>
                <div className="timeline-content">
                  <p className="timeline-label">{step.label}</p>
                  {step.description && (
                    <p className="timeline-description">{step.description}</p>
                  )}
                  {step.date && (
                    <span className="timeline-date">{step.date}</span>
                  )}
                </div>
              </div>
            ))}

            {bill.status === 'in_review' && (
              <div className="detail-action-box">
                <img
                  src="/images/mascot/fox-reading-bill.png"
                  alt=""
                  style={{
                    width: 48,
                    height: 'auto',
                    flexShrink: 0,
                    filter: 'drop-shadow(0 2px 6px rgba(14, 21, 48, 0.10))',
                  }}
                />
                <div>
                  <strong>Action Required</strong>
                  <p>
                    Agent Loop secured a {pct}% discount! Please review and
                    accept.
                  </p>
                </div>
              </div>
            )}

            {/* Agent Loop investigation notes */}
            {(bill.status !== 'submitted') && (
              <div className="agent-notes-section">
                <div className="agent-notes-header">
                  <Search size={14} />
                  <strong>Agent Loop Investigation Notes</strong>
                </div>
                <div className="agent-notes-body">
                  {getNotes(bill, updates).map((note, i) => (
                    <div key={i} className="agent-note-item">
                      <span className="agent-note-icon">{note.icon}</span>
                      <p>{note.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'document' && (
          <BillDocumentViewer bill={bill} />
        )}

        {tab === 'caselog' && (
          /* v3 unified feed: merges status events (bill_updates) with
             chat messages (bill_communication_log) into one chronology.
             CaseFeed now owns its own loading skeleton via the `loading`
             prop, so we don't double up with the LoadingSkeleton wrapper. */
          <CaseFeed updates={updates} commLogs={commLogs} loading={loadingData} />
        )}
      </div>

      {/* Footer */}
      <div className="detail-footer">
        <button
          className="detail-open-case-btn"
          onClick={() => {
            onClose();
            navigate(`/case/${bill.id}`);
          }}
        >
          Open full case file →
        </button>
        <button className="detail-close-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

/* ── Helpers ── */

function getProgressSteps(
  status: BillStatus,
  createdAt: string,
  updates: BillUpdate[],
) {
  const dateStr = new Date(createdAt).toLocaleDateString();

  // info_needed can happen at any stage, show it as a special banner, not a step
  const isInfoNeeded = status === 'info_needed';

  // no_savings and completed are both terminal states
  const isTerminal = status === 'completed' || status === 'no_savings';

  const statusOrder: BillStatus[] = [
    'submitted',
    'in_review',
    'negotiating',
    'completed',
  ];
  // Map no_savings to the completed position for step progression.
  // negotiating_more sits at the same step as negotiating, the contextual
  // "partial win" detail is conveyed via the notes section, not the stepper.
  const effectiveStatus =
    status === 'no_savings'
      ? 'completed'
      : status === 'info_needed'
        ? 'in_review'
        : status === 'negotiating_more'
          ? 'negotiating'
          : status;
  const currentIdx = statusOrder.indexOf(effectiveStatus);

  const statusChangeUpdates = updates.filter(
    (u) => u.update_type === 'status_change',
  );

  function findStatusDate(keyword: string): string | null {
    const match = statusChangeUpdates.find((u) =>
      u.message.toLowerCase().includes(keyword.toLowerCase()),
    );
    return match ? new Date(match.created_at).toLocaleDateString() : null;
  }

  const steps = [
    {
      label: 'Case Filed',
      done: currentIdx >= 0,
      date: dateStr,
      description: 'Your bill has been received. Agent Loop is preparing to investigate.',
    },
    {
      label: isInfoNeeded ? 'Intel Needed' : 'Under Investigation',
      done: currentIdx >= 1,
      date:
        currentIdx >= 1
          ? findStatusDate('review') ||
            findStatusDate('in_review') ||
            (isInfoNeeded ? 'Waiting on you' : 'Agent Loop is on it')
          : null,
      description: isInfoNeeded
        ? 'Agent Loop needs additional information from you to continue.'
        : 'Our clever fox is reviewing your bill line by line, checking for billing errors, upcoding, and overcharges.',
    },
    {
      label: 'Agent Loop Negotiating',
      done: currentIdx >= 2,
      date:
        currentIdx >= 2
          ? findStatusDate('negotiat') || 'Agent Loop is on it'
          : null,
      description: 'Agent Loop is actively negotiating with the provider\'s billing department to reduce your charges.',
    },
    {
      label: status === 'no_savings' ? 'Case Reviewed' : 'Case Closed',
      done: isTerminal,
      date: isTerminal
        ? findStatusDate('complet') ||
          findStatusDate('done') ||
          findStatusDate('no_saving') ||
          'The verdict is in'
        : null,
      description: status === 'no_savings'
        ? 'After thorough review, Agent Loop found the charges are within expected ranges.'
        : 'The investigation is complete. Your savings have been applied.',
    },
  ];

  return steps;
}


/* ── Bill Document Viewer ── */
function BillDocumentViewer({ bill }: { bill: Bill }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const getUrl = async () => {
      try {
        // Try file_url first (direct URL)
        if (bill.file_url) {
          setUrl(bill.file_url);
          setLoading(false);
          return;
        }

        // Otherwise generate a signed URL from storage
        const { data, error: signError } = await supabase.storage
          .from('medical_bills')
          .createSignedUrl(bill.file_path, 3600); // 1 hour

        if (signError || !data?.signedUrl) {
          setError(true);
        } else {
          setUrl(data.signedUrl);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    getUrl();
  }, [bill.file_path, bill.file_url]);

  const fileName = bill.file_path.split('/').pop() || 'document';
  const isPdf = fileName.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);

  if (loading) {
    return (
      <div className="doc-viewer-loading">
        <Clock size={24} />
        <p>Loading document...</p>
      </div>
    );
  }

  if (error || !url) {
    return (
      <div className="doc-viewer-error">
        <FileText size={36} />
        <strong>Unable to load document</strong>
        <p>The uploaded bill could not be retrieved. It may have expired or been moved.</p>
      </div>
    );
  }

  return (
    <div className="doc-viewer">
      <div className="doc-viewer-toolbar">
        <span className="doc-viewer-filename">
          <FileText size={14} /> {fileName}
        </span>
        <div className="doc-viewer-actions">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="doc-viewer-btn"
            title="Open in new tab"
          >
            <ExternalLink size={14} /> Open
          </a>
          <a
            href={url}
            download={fileName}
            className="doc-viewer-btn"
            title="Download"
          >
            <Download size={14} /> Download
          </a>
        </div>
      </div>
      <div className="doc-viewer-content">
        {isPdf ? (
          <iframe
            src={url}
            title="Uploaded bill"
            className="doc-viewer-iframe"
          />
        ) : isImage ? (
          <img src={url} alt="Uploaded bill" className="doc-viewer-img" />
        ) : (
          <div className="doc-viewer-fallback">
            <FileText size={48} />
            <p>Preview not available for this file type.</p>
            <a href={url} target="_blank" rel="noopener noreferrer" className="doc-viewer-btn primary">
              <ExternalLink size={14} /> Open file
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Agent Loop investigation notes ── */
function getNotes(
  bill: Bill,
  updates: BillUpdate[],
): { icon: string; text: string }[] {
  const notes: { icon: string; text: string }[] = [];
  const name = bill.provider_name || bill.display_name || 'the provider';

  // Pull real notes from updates
  const noteUpdates = updates.filter((u) => u.update_type === 'note');
  for (const note of noteUpdates) {
    notes.push({ icon: '📝', text: note.message });
  }

  // If no real notes, generate contextual ones based on what we know
  if (notes.length === 0) {
    if (bill.status === 'submitted') {
      notes.push({
        icon: '📋',
        text: `Bill received from ${name}. Agent Loop is starting the investigation.`,
      });
    }

    if (bill.status === 'in_review') {
      notes.push({
        icon: '🔍',
        text: `Agent Loop is reviewing your bill from ${name}, checking for billing errors, upcoding, and overcharges.`,
      });
      if (bill.insurance_carrier) {
        notes.push({
          icon: '🛡️',
          text: `Cross-referencing charges with ${bill.insurance_carrier} coverage policies.`,
        });
      }
    }

    if (bill.status === 'negotiating') {
      notes.push({
        icon: '📞',
        text: `Agent Loop is actively negotiating with ${name}'s billing department.`,
      });
      if (bill.bill_amount) {
        notes.push({
          icon: '💰',
          text: `Original bill: $${bill.bill_amount.toLocaleString()}. Working to reduce this amount.`,
        });
      }
    }

    if (bill.status === 'negotiating_more') {
      const saved = bill.amount_saved ?? 0;
      notes.push({
        icon: '🎯',
        text: saved > 0
          ? `Partial win secured: $${saved.toLocaleString()} already off. Agent Loop is still pushing ${name} for further reductions.`
          : `Initial discount secured. Agent Loop is still pushing ${name} for further reductions.`,
      });
    }

    if (bill.status === 'completed' && bill.amount_saved) {
      notes.push({
        icon: '✅',
        text: `Case closed! Reduced your bill by $${bill.amount_saved.toLocaleString()}.`,
      });
      notes.push({
        icon: '🦊',
        text: `Agent Loop found leverage points and negotiated directly with ${name}.`,
      });
    }

    if (bill.status === 'no_savings') {
      notes.push({
        icon: '📊',
        text: `After thorough review, Agent Loop determined the charges from ${name} are within expected ranges.`,
      });
    }

    if (bill.status === 'info_needed') {
      notes.push({
        icon: '⚠️',
        text: 'Agent Loop needs additional information to continue the investigation. Please check your email for details.',
      });
    }
  }

  return notes;
}
